// WebSocket status flow removed in client‑driven mode
import { useWebSocketPayment } from "@/hooks/useWebSocketPayment"
import { useCashRegistersQuery } from "@/query/cashRegisterQuery"
import { useCurrentUserQuery } from "@/query/currentUserQuery"
import {
  StripePaymentIntent,
  useCancelPaymentIntentMutation,
  useCreatePaymentIntentMutation,
  useProcessPaymentIntentMutation,
  useStripeReadersQuery,
} from "@/query/stripeTerminalQuery"
import { getCurrentCashRegister } from "@/utils/functions"
import { StripeTerminalReader } from "@okeys/shared-utils"
import { useCallback, useEffect, useRef, useState } from "react"
import type { PaymentActions, PaymentState, PaymentStatus } from "../types"

interface UseStripeTerminalProps {
  amount: number
  onPaymentSuccess?: (paymentIntentId: string) => void
  onPaymentFailed?: (error: string) => void
  onPaymentCancelled?: () => void
  onReset?: () => void
  simulate?: boolean
}

export const useStripeTerminal = ({
  amount,
  onPaymentSuccess,
  onPaymentFailed,
  onPaymentCancelled,
}: UseStripeTerminalProps): PaymentState &
  PaymentActions & {
    isPaymentCancelledRef: React.MutableRefObject<boolean | null>
    isPaymentSuccessRef: React.MutableRefObject<boolean | null>
    isPaymentFailedRef: React.MutableRefObject<boolean | null>
    createPaymentIntentMutation: ReturnType<
      typeof useCreatePaymentIntentMutation
    >
  } => {
  // État du paiement
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>("idle")
  const [errorMessage, setErrorMessage] = useState<string>("")

  const { currentUser } = useCurrentUserQuery()
  const { cashRegisters } = useCashRegistersQuery()
  const { readers } = useStripeReadersQuery()

  // collectionStateTempId removed - no longer used
  const currentCashRegister = getCurrentCashRegister(currentUser, cashRegisters)

  // Références Stripe Terminal
  const paymentIntentDataRef = useRef<StripePaymentIntent | null>(null)
  const isPaymentCancelledRef = useRef<boolean>(false)
  const isPaymentSuccessRef = useRef<boolean>(false)
  const isPaymentFailedRef = useRef<boolean>(false)

  // Hooks pour Stripe Terminal
  const createPaymentIntentMutation = useCreatePaymentIntentMutation()
  const processPaymentIntentMutation = useProcessPaymentIntentMutation()
  const cancelPaymentIntentMutation = useCancelPaymentIntentMutation()

  // WebSocket supprimé en mode client‑driven
  const { disconnect, connect } = useWebSocketPayment()

  // Démarrer un monitoring (SDK) en logging only
  useEffect(() => {
    setPaymentStatus("idle")
    return () => {
      disconnect()
    }
  }, [disconnect])

  // Flux privilégié: client-driven (connectReader -> collect -> process)

  const createPaymentIntent = async (reader: StripeTerminalReader) => {
    setPaymentStatus("preparing_terminal")

    const paymentIntentData = await createPaymentIntentMutation.mutateAsync({
      amount: Math.round(amount * 100),
      currency: "eur",
      payment_method_types: ["card_present"],
      capture_method: "automatic",
      metadata: {
        // collectionStateTempId removed - no longer used
        cashRegisterId: currentCashRegister?.caisseId || "",
        readerId: reader.id,
      },
    })

    paymentIntentDataRef.current = paymentIntentData

    return paymentIntentData
  }

  // Processus de paiement complet
  const processPayment = async () => {
    let paymentIntentData: StripePaymentIntent | null = null

    setErrorMessage("")
    isPaymentCancelledRef.current = false

    // Étape 1: Découvrir et connecter le lecteur
    const reader = readers?.[0]

    if (!reader) {
      throw new Error("Aucun lecteur trouvé")
    }

    if (isPaymentCancelledRef.current) return

    // Étape 2: Créer le PaymentIntent (serveur), puis collect/process côté client via SDK

    try {
      paymentIntentData = await createPaymentIntent(reader)
    } catch (error) {
      console.error(
        "[StripeTerminal] 📤 Erreur lors de la création du PaymentIntent:",
        error
      )
      setPaymentStatus("failed_create")
      onPaymentFailed?.(
        error instanceof Error
          ? error.message
          : "Erreur lors de la création du PaymentIntent"
      )
      throw error
    }

    if (isPaymentCancelledRef.current) return

    try {
      await connect({
        paymentIntentId: paymentIntentDataRef.current?.id || "",
        onSuccess: (data) => {
          setPaymentStatus(data.status as PaymentStatus)
          onPaymentSuccess?.(data.paymentIntentId || "")
          isPaymentSuccessRef.current = true
        },
        onFailure: (data) => {
          setPaymentStatus(data.status as PaymentStatus)
          isPaymentFailedRef.current = true
        },
      })
    } catch (error) {
      console.error(
        "[StripeTerminal] 📤 Erreur lors de la connexion au lecteur:",
        error
      )
      setPaymentStatus("failed_connect")
      onPaymentFailed?.(
        error instanceof Error
          ? error.message
          : "Erreur lors de la connexion au lecteur"
      )
      throw error
    }

    if (isPaymentCancelledRef.current) return

    // Étape 3: Collecter et traiter le paiement côté client via SDK
    if (!paymentIntentData.client_secret) {
      throw new Error(
        "Le client_secret est manquant. Assurez‑vous que l'API de création retourne client_secret."
      )
    }

    try {
      await processPaymentIntentMutation.mutateAsync({
        paymentIntentId: paymentIntentDataRef.current?.id || "",
        readerId: reader.id,
      })
    } catch (error) {
      console.error(
        "[StripeTerminal] 📤 Erreur lors du traitement du paiement:",
        error
      )
      setPaymentStatus("failed_process")
      onPaymentFailed?.(
        error instanceof Error
          ? error.message
          : "Erreur lors du traitement du paiement"
      )
      throw error
    }

    await waitResultPayment()
  }

  // Fonction pour annuler le paiement
  const cancelPayment = useCallback(async () => {
    if (paymentStatus === "cancelling") return

    isPaymentCancelledRef.current = true
    setPaymentStatus("cancelling")

    try {
      // Annuler le PaymentIntent sur Stripe
      if (paymentIntentDataRef.current?.id) {
        try {
          await cancelPaymentIntentMutation.mutateAsync(
            paymentIntentDataRef.current.id
          )
        } catch (stripeError) {
          console.warn("Erreur lors de l'annulation sur Stripe:", stripeError)
        }
      }

      // Réinitialiser l'état
      setPaymentStatus("idle")
      setErrorMessage("")
      paymentIntentDataRef.current = null

      onPaymentCancelled?.()
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? `Erreur d'annulation: ${error.message}`
          : "Erreur lors de l'annulation du paiement"
      )
      setPaymentStatus("failed_cancel")
    }
  }, [
    paymentIntentDataRef,
    cancelPaymentIntentMutation,
    onPaymentCancelled,
    paymentStatus,
  ])

  const waitResultPayment = useCallback(async () => {
    const start = Date.now()
    const timeoutMs = 180000 // 3 minutes

    await new Promise<void>((resolve, reject) => {
      const checkInterval = setInterval(() => {
        // Vérifier si annulé
        if (isPaymentCancelledRef.current) {
          clearInterval(checkInterval)
          resolve()
          return
        }

        // Vérifier le timeout
        if (Date.now() - start > timeoutMs) {
          clearInterval(checkInterval)
          cancelPayment()
          reject(
            new Error(
              "Timeout: Le paiement n'a pas abouti dans le délai imparti"
            )
          )
          return
        }

        // Vérifier si le paiement est terminé
        if (paymentStatus === "succeeded" || paymentStatus === "failed") {
          clearInterval(checkInterval)
          resolve()

          return
        }
      }, 500)
    })
  }, [paymentStatus, cancelPayment])

  // Fonction pour réinitialiser l'état du paiement
  const resetPaymentState = useCallback(async () => {
    setPaymentStatus("idle")
    setErrorMessage("")
    paymentIntentDataRef.current = null
    isPaymentCancelledRef.current = false
    isPaymentSuccessRef.current = false
    isPaymentFailedRef.current = false
  }, [])

  const handleRetry = useCallback(async () => {
    resetPaymentState()
  }, [resetPaymentState])

  return {
    // État
    paymentStatus,
    errorMessage,
    paymentIntentId: paymentIntentDataRef.current?.id || null,
    isCancelling: paymentStatus === "cancelling",
    isLoadingToken: false, // Token is handled by the WebSocket connection

    // Actions
    processPayment,
    cancelPayment,
    handleRetry,
    resetPaymentState,

    // Références
    isPaymentCancelledRef,
    isPaymentSuccessRef,
    isPaymentFailedRef,

    // Données utiles
    createPaymentIntentMutation,
  }
}
